import { useEffect, useMemo, useRef, useState } from "react";
import { myId } from "../lib/me";
import { supabase } from "../lib/supabase";
import { describeOutcome, planWrites, summarise, type ExistingAssignment } from "../lib/assign";
import { tellTheClient } from "../lib/assignPush";
import { libraryRows } from "../lib/builder";
import { shortDate } from "../lib/weekLine";
import {
  ageBadge, keepOrFirst, mostRecentFirst, pickerOverline, pickerSize, searchByName, type PickerRow,
} from "../lib/assignPicker";
import { PhoneOverview } from "./PhoneOverview";

/* ══════════════════════════════════════════════════════════════════════════════════════════════
   W63 — "ASSIGN A WORKOUT", THEIR DIALOG (EVERFIT-MAP §8a-ter; re-read live on his account
   2026-09-24, read-only, closed with its own ✕).

   His report the same morning: *"I tried assigning a workout and it's almost impossible."* Ours was
   a native `<select>` of every template in a form BELOW the calendar, reached by a `+` that only
   appeared on hover and then ticked a checkbox in that form — measured: 5 steps and a scroll, where
   theirs is 2 (the day's disc, then `Select`).

   Copied at depth 1: the title; `Create New` at its right; a search by name with no submit; an
   overline naming the mode (`MOST RECENT (N)` / `RESULTS (N)` / `RESULTS` + `No result found.`);
   rows `name · N Exercises · M Sections · age`; the first row chosen on arrival; a phone-shaped
   preview of the chosen workout at the right; `Select` at the foot.
   ⚠️ DEPARTURES, filed in §6b rather than improved here: no `Video Workout` tab (their upsell
   gate), no `Everfit AI`, no `Tags` (we hold no tags), no owner avatar (one coach per library).
   ⚠️ THE ROWS ARE REAL RADIOS — theirs are not in the accessibility tree at all (§8a-ter's sixth
   screen in a row); a radio group gives arrow keys and "3 of 14" for free.

   ⚠️ IT IS OUR ONE WINDOW MECHANISM: a native `<dialog>` with `showModal`, an ADDRESS
   (`?view=find-a-workout&date=…`, theirs verbatim), and closing is a NAVIGATION the parent performs
   — never a second modal system.
   ⚠️ THE WRITE IS THE SAME RPC EVERY OTHER ASSIGN USES (`assign_coach_workout`, copy-on-assign),
   predicted against the one-source-per-day index first (`planWrites`) and read back through
   `describeOutcome`, so a 23505 is said as "already has this workout", never as a raw error.
   ══════════════════════════════════════════════════════════════════════════════════════════════ */

type Row = PickerRow & { description: string | null; rootId: string };

export function AssignWorkoutWindow({ clientId, day, existing, today, onClose, onAssigned, buildHref }: {
  clientId: string;
  /** The day the disc named — `YYYY-MM-DD`, exactly what `scheduled_date` stores. */
  day: string;
  existing: ExistingAssignment[];
  today: Date;
  onClose: () => void;
  /** Called with the sentence to show on the page once the write has landed. */
  onAssigned: (said: string) => void;
  /** William's item 4 — where `Build a workout` goes: the builder, empty, for THIS client and day (`clientWorkoutNew`). */
  buildHref: string;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  /** W130 — the coach is walking the chosen workout inside the preview's phone. */
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const el = dialogRef.current;
    if (el !== null && !el.open) el.showModal();
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // TEMPLATES only, and never an archived one — the same read the old panel made, for its
      // stated reasons (a client copy offered back would lose the root the duplicate guard keys on).
      const r = await supabase
        .from("coach_workouts")
        .select("id, name, description, root_workout_id, coach_workout_sections(id, name, position, coach_workout_exercises(id, exercise_id))")
        .eq("coach_id", await myId())
        .is("client_id", null)
        .is("archived_at", null)
        .order("name");
      if (cancelled) return;
      if (r.error) { setLoadError(r.error.message); return; }
      const { data: assigned } = await supabase
        .from("coach_assignments")
        .select("workout_id, root_workout_id, scheduled_date");
      if (cancelled) return;
      const roots = new Map((r.data ?? []).map((w) => [w.id, w.root_workout_id ?? w.id]));
      const lib = libraryRows(r.data ?? [], assigned ?? []);
      const next = mostRecentFirst(lib.map((l) => ({
        id: l.id, name: l.name, exercises: l.exercises, sections: l.sections,
        lastUsedISO: l.lastUsedISO, description: l.description, rootId: roots.get(l.id) ?? l.id,
      })));
      setRows(next);
      setChosenId(next[0]?.id ?? null);
    }
    void load();
    return () => { cancelled = true; };
  }, []);

  const shown = useMemo(() => (rows === null ? [] : searchByName(rows, query)), [rows, query]);
  // Theirs re-selects the first result as you type, so the preview always follows the list.
  useEffect(() => { setChosenId((id) => keepOrFirst(shown, id)); }, [shown]);
  const chosen = shown.find((r) => r.id === chosenId) ?? null;

  async function select() {
    if (busy) return;
    if (chosen === null) { setSaid(query.trim() === "" ? "Choose a workout." : "Nothing matches — clear the search."); return; }
    const plan = planWrites([day], chosen.rootId, existing);
    if (plan[0]?.action === "alreadyAssigned") {
      setSaid(`${shortDate(day)} already has ${chosen.name}.`);
      return;
    }
    setBusy(true);
    setSaid(null);
    const r = await supabase.rpc("assign_coach_workout", {
      p_workout_id: chosen.id, p_client_id: clientId, p_scheduled_date: day, p_note: "",
    });
    setBusy(false);
    const outcome = describeOutcome(day, r.error);
    if (outcome.kind === "assigned") {
      tellTheClient(r.data ?? null, 1);
      onAssigned(`${chosen.name} assigned to ${shortDate(day)}.`);
      return;
    }
    setSaid(outcome.kind === "alreadyAssigned" ? `${shortDate(day)} already has ${chosen.name}.` : summarise([outcome]));
  }

  return (
    <dialog ref={dialogRef} className="ht-window assign-workout-window"
            aria-labelledby="assign-workout-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="ht-window-frame assign-workout-frame">
        <header className="ht-window-head assign-workout-head">
          <div className="ht-window-titles">
            <p className="page-eyebrow">{shortDate(day)}</p>
            <h2 className="ht-title" id="assign-workout-title">Assign a workout</h2>
          </div>
          {/* 🔴 WILLIAM'S ITEM 4 (2026-10-15) — `Build a workout` REPLACES `Create new` IN THIS SLOT. `Create new` left the
              client for the LIBRARY's builder (a template, then back here to assign it); William's ask is to build IN the
              week (*"create the workout in this section and then have the option to save it as a template"*), which is
              what Everfit's `Create New` in this window does. Two doors side by side that both read "make a workout"
              would repeat each other (CLAUDE.md: never repeat a neighbouring line); a template is still made from here —
              the builder's `Save to library` — and from the Library's own `+`. Claude's call, said in DECISIONS. */}
          <a className="ht-button assign-workout-create" href={buildHref}>Build a workout</a>
          <button type="button" className="ht-button ht-button-quiet ht-window-close"
                  onClick={onClose}>Close</button>
        </header>

        <div className="ht-window-body assign-workout-body">
          <div className="assign-workout-list">
            <label className="assign-workout-search">
              <span className="ht-visually-hidden">Search by workout name</span>
              <input ref={searchRef} type="search" className="ht-input" placeholder="Search by workout name"
                     value={query} onChange={(e) => { setQuery(e.target.value); setSaid(null); }} />
            </label>
            {loadError !== null && (
              <p className="ht-callout" role="alert">Could not load your workouts: {loadError}</p>
            )}
            {rows === null && loadError === null && (
              <p className="ht-callout loading-line" aria-live="polite">Loading your workouts…</p>
            )}
            {rows !== null && (
              <fieldset className="assign-workout-rows">
                <legend className="ht-overline assign-workout-overline">
                  {pickerOverline(query, shown.length)}
                </legend>
                {shown.length === 0 && (
                  <p className="ht-callout assign-workout-none">
                    {rows.length === 0 ? "You have no workouts yet. Create one first." : "No result found."}
                  </p>
                )}
                {shown.map((r) => (
                  <label key={r.id} className="assign-workout-row">
                    <input type="radio" name="assign-workout-pick" checked={r.id === chosenId}
                           onChange={() => { setChosenId(r.id); setSaid(null); }} />
                    <span className="assign-workout-row-text">
                      <span className="ht-body-medium assign-workout-row-name">{r.name}</span>
                      <span className="ht-caption assign-workout-row-size">{pickerSize(r)}</span>
                    </span>
                    <span className="ht-caption assign-workout-row-age">{ageBadge(r.lastUsedISO, today)}</span>
                  </label>
                ))}
              </fieldset>
            )}
          </div>

          {/* Their phone mockup, rendering the CHOSEN workout — the calendar's own tiles, so the
              preview and the day cell cannot describe one workout two ways. */}
          <aside className="assign-workout-preview" aria-labelledby="assign-workout-preview-h">
            {chosen === null ? (
              <p className="ht-caption assign-workout-preview-empty">Nothing chosen.</p>
            ) : (
              <>
                {/* W66 (2026-09-24, his word): the phone IS the client's workout overview, at an
                    iPhone Pro's ratio — never a list in a rounded box. */}
                <h3 id="assign-workout-preview-h" className="ht-visually-hidden">{chosen.name} · {pickerSize(chosen)}</h3>
                <PhoneOverview key={chosen.id} workoutId={chosen.id} startable onPlayingChange={setPlaying} />
                {playing && <p className="phone-preview-line assign-workout-preview-line" role="status">Preview — nothing is saved</p>}
              </>
            )}
          </aside>
        </div>

        <footer className="ht-window-foot assign-workout-foot">
          {said !== null && <p className="ht-callout assign-workout-said" role="status">{said}</p>}
          <button type="button" className="ht-button ht-button-primary assign-workout-select"
                  disabled={busy} onClick={() => { void select(); }}>
            {busy ? "Assigning…" : "Select"}
          </button>
        </footer>
      </div>
    </dialog>
  );
}
